import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { adminAPI } from '@/api/client';
import { toast } from 'sonner';
import { Mail, Send, Loader2, Calendar, Filter } from 'lucide-react';

const EmailNotificationSender = () => {
  // New Exam Notification States
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [pendingExams, setPendingExams] = useState(null);
  const [notificationResults, setNotificationResults] = useState(null);
  const [loadingPending, setLoadingPending] = useState(false);
  const [createdAfterDate, setCreatedAfterDate] = useState('');
  const [useCreatedAfterFilter, setUseCreatedAfterFilter] = useState(false);
  
  // Fetch pending exam notifications on mount
  useEffect(() => {
    fetchPendingNotifications();
  }, []);
  
  const fetchPendingNotifications = async (filterDate = null) => {
    setLoadingPending(true);
    try {
      const dateToUse = filterDate !== null ? filterDate : (useCreatedAfterFilter ? createdAfterDate : null);
      const response = await adminAPI.getPendingExamNotifications(dateToUse);
      setPendingExams(response.data);
    } catch (error) {
      console.error('Failed to fetch pending notifications:', error);
    } finally {
      setLoadingPending(false);
    }
  };
  
  const handleApplyDateFilter = () => {
    if (useCreatedAfterFilter && !createdAfterDate) {
      toast.error('অনুগ্রহ করে একটি date select করুন');
      return;
    }
    fetchPendingNotifications(useCreatedAfterFilter ? createdAfterDate : null);
  };
  
  const handleSendExamNotifications = async () => {
    if (!pendingExams?.has_pending) {
      toast.info('কোন নতুন exam নেই notification পাঠানোর জন্য');
      return;
    }
    
    const totalCD = pendingExams.pending_exams.total_cd || 0;
    const totalPB = pendingExams.pending_exams.total_pb || 0;
    const cdStudents = pendingExams.students.cd_students || 0;
    const pbStudents = pendingExams.students.pb_students || 0;
    
    // Build confirmation message showing only categories with exams
    let studentInfo = '';
    if (totalCD > 0) {
      studentInfo += `CD Students: ${cdStudents} জন (${totalCD}টি exam সম্পর্কে)\n`;
    }
    if (totalPB > 0) {
      studentInfo += `PB Students: ${pbStudents} জন (${totalPB}টি exam সম্পর্কে)\n`;
    }
    
    // Show which categories will be skipped
    let skipInfo = '';
    if (totalCD === 0 && cdStudents > 0) {
      skipInfo += `\n⏭️ CD Students (${cdStudents} জন) skip হবে - কোন CD exam নেই\n`;
    }
    if (totalPB === 0 && pbStudents > 0) {
      skipInfo += `\n⏭️ PB Students (${pbStudents} জন) skip হবে - কোন PB exam নেই\n`;
    }
    
    let filterInfo = '';
    if (useCreatedAfterFilter && createdAfterDate) {
      filterInfo = `\n📅 Filter: শুধু ${createdAfterDate} এর পরে তৈরি exams\n`;
    }
    
    const confirmMsg = `এটা নিম্নলিখিত students দের email পাঠাবে:\n\n` +
      studentInfo +
      skipInfo +
      filterInfo +
      `\n⏱️ প্রতিটি email এর মধ্যে 2 সেকেন্ড gap থাকবে।\n\n` +
      `Continue করতে চান?`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    setSendingNotifications(true);
    setNotificationResults(null);
    
    try {
      const dateToUse = useCreatedAfterFilter ? createdAfterDate : null;
      const response = await adminAPI.sendNewExamNotifications(dateToUse);
      setNotificationResults(response.data);
      
      const totalSent = (response.data.cd_students_notified || 0) + (response.data.pb_students_notified || 0);
      const failed = response.data.failed_emails || 0;
      
      if (failed > 0) {
        toast.warning(`✅ ${totalSent} জনকে email পাঠানো হয়েছে, ${failed}টি failed (Email Queue তে আছে)`);
      } else if (totalSent > 0) {
        toast.success(`✅ ${totalSent} জন student কে সফলভাবে email পাঠানো হয়েছে!`);
      } else {
        toast.info('কোন email পাঠানো হয়নি');
      }
      
      // Refresh pending count
      fetchPendingNotifications();
      
    } catch (error) {
      console.error('Send notifications failed:', error);
      toast.error(error.response?.data?.detail || 'Notification পাঠাতে সমস্যা হয়েছে');
    } finally {
      setSendingNotifications(false);
    }
  };

  return (
    <div className="p-4">
      <Card className="border-2 border-green-300 bg-gradient-to-br from-green-50 to-emerald-50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="h-5 w-5 text-green-600" />
            📧 New Exam Slot Notification
            {pendingExams?.has_pending && (
              <Badge className="bg-green-500 text-white animate-pulse">
                New Exams Available!
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            নতুন Exam slot তৈরি করার পর students দের email notification পাঠান।
            Category অনুযায়ী CD students কে CD exams এবং PB students কে PB exams এর notification যাবে।
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Pending Exams Summary */}
          {loadingPending ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading pending exams...
            </div>
          ) : pendingExams ? (
            <div className="grid grid-cols-2 gap-4">
              {/* CD Exams */}
              <div className={`rounded-lg p-4 border ${
                pendingExams.pending_exams.total_cd > 0 
                  ? 'bg-blue-50 border-blue-200' 
                  : 'bg-gray-50 border-gray-200 opacity-60'
              }`}>
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-semibold text-blue-800">CD (Computer Delivered)</h4>
                  {pendingExams.pending_exams.total_cd > 0 ? (
                    <Badge className="bg-green-500 text-white text-xs">✓ Will Send</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">Skip</Badge>
                  )}
                </div>
                <div className="space-y-1 text-sm">
                  <p className="text-gray-700">
                    Partial Exams: <strong className="text-blue-600">{pendingExams.pending_exams.cd_partial || 0}</strong>
                  </p>
                  <p className="text-gray-700">
                    Speaking Exams: <strong className="text-purple-600">{pendingExams.pending_exams.shared_speaking || 0}</strong>
                    <span className="text-xs text-gray-500 ml-1">(shared)</span>
                  </p>
                  <p className="text-gray-700">
                    Mock Tests: <strong className="text-blue-600">{pendingExams.pending_exams.cd_mock || 0}</strong>
                  </p>
                  <hr className="my-2" />
                  <p className="text-gray-700">
                    Students: <strong className="text-blue-700">{pendingExams.students.cd_students || 0}</strong> জন
                    {pendingExams.pending_exams.total_cd === 0 && (
                      <span className="text-xs text-gray-500 ml-1">(no email)</span>
                    )}
                  </p>
                </div>
              </div>
              
              {/* PB Exams */}
              <div className={`rounded-lg p-4 border ${
                pendingExams.pending_exams.total_pb > 0 
                  ? 'bg-orange-50 border-orange-200' 
                  : 'bg-gray-50 border-gray-200 opacity-60'
              }`}>
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-semibold text-orange-800">PB (Paper Based)</h4>
                  {pendingExams.pending_exams.total_pb > 0 ? (
                    <Badge className="bg-green-500 text-white text-xs">✓ Will Send</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-xs">Skip</Badge>
                  )}
                </div>
                <div className="space-y-1 text-sm">
                  <p className="text-gray-700">
                    Partial Exams: <strong className="text-orange-600">{pendingExams.pending_exams.pb_partial || 0}</strong>
                  </p>
                  <p className="text-gray-700">
                    Speaking Exams: <strong className="text-purple-600">{pendingExams.pending_exams.shared_speaking || 0}</strong>
                    <span className="text-xs text-gray-500 ml-1">(shared)</span>
                  </p>
                  <p className="text-gray-700">
                    Mock Tests: <strong className="text-orange-600">{pendingExams.pending_exams.pb_mock || 0}</strong>
                  </p>
                  <hr className="my-2" />
                  <p className="text-gray-700">
                    Students: <strong className="text-orange-700">{pendingExams.students.pb_students || 0}</strong> জন
                    {pendingExams.pending_exams.total_pb === 0 && (
                      <span className="text-xs text-gray-500 ml-1">(no email)</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
          
          {/* Date Filter Section */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="h-4 w-4 text-purple-600" />
              <Label className="font-semibold text-purple-800">📅 Date Filter (Optional)</Label>
            </div>
            <p className="text-sm text-purple-700 mb-3">
              পুরানো exams বাদ দিতে চাইলে, একটি date select করুন। শুধু ওই date এর পরে তৈরি exams দেখাবে এবং send হবে।
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="useCreatedAfterFilter" 
                  checked={useCreatedAfterFilter}
                  onCheckedChange={(checked) => {
                    setUseCreatedAfterFilter(checked);
                    if (!checked) {
                      setCreatedAfterDate('');
                      fetchPendingNotifications(null);
                    }
                  }}
                />
                <label htmlFor="useCreatedAfterFilter" className="text-sm cursor-pointer text-gray-700">
                  Date filter ব্যবহার করুন
                </label>
              </div>
              
              {useCreatedAfterFilter && (
                <>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-purple-600" />
                    <Input
                      type="datetime-local"
                      value={createdAfterDate}
                      onChange={(e) => setCreatedAfterDate(e.target.value)}
                      className="w-56 border-purple-300 focus:border-purple-500"
                      placeholder="Select date & time"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleApplyDateFilter}
                    disabled={loadingPending || !createdAfterDate}
                    className="border-purple-400 text-purple-700 hover:bg-purple-100"
                  >
                    Apply Filter
                  </Button>
                </>
              )}
            </div>
            {useCreatedAfterFilter && createdAfterDate && pendingExams?.created_after_filter && (
              <div className="mt-3 text-sm bg-purple-100 rounded p-2">
                <strong className="text-purple-800">Active Filter:</strong> 
                <span className="text-purple-700 ml-1">
                  {new Date(createdAfterDate).toLocaleString('en-BD', { 
                    dateStyle: 'medium', 
                    timeStyle: 'short' 
                  })} এর পরে তৈরি exams
                </span>
              </div>
            )}
          </div>
          
          {/* Info Box */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
            <p className="text-amber-800">
              <strong>⚠️ গুরুত্বপূর্ণ:</strong> প্রতিটি email এর মধ্যে 2 সেকেন্ড gap থাকবে rate limit এড়াতে।
              Failed emails Email Queue তে যাবে এবং পরে Resend করা যাবে।
            </p>
          </div>
          
          {/* Send Button */}
          <div className="flex gap-3">
            <Button 
              onClick={handleSendExamNotifications}
              disabled={sendingNotifications || !pendingExams?.has_pending}
              className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
            >
              {sendingNotifications ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending Emails...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  📨 Send New Exam Notifications
                </>
              )}
            </Button>
            <Button 
              variant="outline"
              onClick={() => fetchPendingNotifications()}
              disabled={loadingPending}
            >
              🔄 Refresh
            </Button>
          </div>
          
          {/* Results */}
          {notificationResults && (
            <div className={`p-4 rounded-lg border-2 ${notificationResults.failed_emails > 0 ? 'bg-yellow-50 border-yellow-300' : 'bg-green-50 border-green-300'}`}>
              <h4 className="font-semibold text-gray-800 mb-3">📊 Notification Results:</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-white p-3 rounded border">
                  <p className="text-gray-600">CD Students Notified</p>
                  <p className="text-2xl font-bold text-blue-600">{notificationResults.cd_students_notified || 0}</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-gray-600">PB Students Notified</p>
                  <p className="text-2xl font-bold text-orange-600">{notificationResults.pb_students_notified || 0}</p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-gray-600">Failed Emails</p>
                  <p className={`text-2xl font-bold ${notificationResults.failed_emails > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {notificationResults.failed_emails || 0}
                  </p>
                </div>
                <div className="bg-white p-3 rounded border">
                  <p className="text-gray-600">Skipped (No Email)</p>
                  <p className="text-2xl font-bold text-gray-500">{notificationResults.skipped_students || 0}</p>
                </div>
              </div>
              {notificationResults.failed_emails > 0 && (
                <p className="mt-3 text-sm text-amber-700">
                  ⚠️ Failed emails Email Queue তে আছে। Email Queue tab থেকে Resend করুন।
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailNotificationSender;
